import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, Sparkles, Loader2, CheckCircle, TrendingUp, Activity, Shield, Zap } from 'lucide-react';
import axios from 'axios';
import Logo from '../components/Logo';

const Feature = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-2">
    <Icon size={14} style={{ color: '#60A5FA' }} className="flex-shrink-0" />
    <span className="text-sm" style={{ color: 'rgba(203,213,225,0.85)' }}>{text}</span>
  </div>
);

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) { toast.error('Please enter your credentials'); return; }
    setLoading(true);
    try {
      const data = await login(username, password);
      // Redirect coaches to their dashboard
      if (data && data.user && data.user.role === 'coach') {
        navigate('/coach');
      } else {
        navigate('/');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Incorrect username or password');
    } finally { setLoading(false); }
  };

  const handleDemoLogin = async () => {
    setDemoLoading(true);
    try {
      toast.loading('Preparing demo…', { id: 'demo' });
      await axios.post(`${API}/seed-demo`);
      toast.dismiss('demo');
      await login('demo', 'Demo@2024');
      navigate('/');
      toast.success('Welcome! 90 days of sample data loaded.');
    } catch { toast.dismiss('demo'); toast.error('Demo failed. Try again.'); }
    finally { setDemoLoading(false); }
  };

  const inputStyle = { background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.12)', color: 'white' };

  return (
    <div className="min-h-screen flex" style={{ background: '#0A0F1E' }}>

      {/* LEFT: Brand panel */}
      <div className="hidden lg:flex flex-col flex-1 relative overflow-hidden px-14 py-12">
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(900px circle at 15% 10%, rgba(30,64,175,0.22), rgba(10,15,30,0) 60%)'
        }} />
        <div className="relative z-10 flex flex-col h-full">
          <Logo size={40} showText light />
          
          <div className="flex-1 flex flex-col justify-center max-w-md py-4">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4 self-start"
              style={{ background: 'rgba(37,99,235,0.15)', border: '1px solid rgba(37,99,235,0.3)' }}>
              <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
              <span className="text-xs font-semibold" style={{ color: '#93C5FD' }}>Trusted by Full Arch practices nationwide</span>
            </div>
            
            <h1 className="font-heading text-4xl font-bold text-white leading-tight mb-2.5" style={{ letterSpacing: '-0.02em' }}>
              Know your implant<br />
              program's <span style={{ color: '#60A5FA' }}>true ROI.</span>
            </h1>
            
            <p className="text-base mb-6 leading-relaxed" style={{ color: 'rgba(148,163,184,0.85)' }}>
              Multi-location Full Arch analytics — consult funnel, close rates, marketing ROI, and revenue all in one dashboard.
            </p>
            
            <div className="space-y-2.5 mb-6">
              <Feature icon={TrendingUp} text="Same-day, normalized, 30-day & 90-day close rate tracking" />
              <Feature icon={Activity} text="Full Arch marketing ROI by source — CPL, cost per arch, ROAS" />
              <Feature icon={Shield} text="Multi-location goals, staff forms, audit log & CSV exports" />
              <Feature icon={Zap} text="Visual consult funnel from leads to arches sold" />
            </div>
            
            {/* Testimonial */}
            <div className="mb-6 p-4 rounded-xl relative overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(37,99,235,0.08), rgba(29,78,216,0.05))',
                border: '1px solid rgba(96,165,250,0.15)',
                backdropFilter: 'blur(10px)'
              }}>
              <div className="absolute -top-1 -left-1 text-6xl font-serif opacity-8" style={{ color: '#60A5FA' }}>"</div>
              <div className="relative z-10">
                <p className="text-sm leading-relaxed mb-4 italic" style={{ color: 'rgba(226,232,240,0.9)' }}>
                  This dashboard transformed how we track our Full Arch program. The normalized close rates and per-location goals finally give us the real picture.
                </p>
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs"
                    style={{ background: 'rgba(37,99,235,0.25)', color: '#93C5FD', border: '1.5px solid rgba(96,165,250,0.3)' }}>
                    MC
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">Dr. Michael Chen</p>
                    <p className="text-xs" style={{ color: 'rgba(148,163,184,0.6)' }}>Smile Innovations · 4 locations</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Pricing CTA */}
            <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div className="flex items-baseline gap-1.5 mb-1">
                <span className="text-2xl font-bold text-white font-heading">$249</span>
                <span className="text-sm" style={{ color: 'rgba(148,163,184,0.7)' }}>/month</span>
              </div>
              <p className="text-xs mb-3" style={{ color: 'rgba(148,163,184,0.65)' }}>First location included · +$29/mo per extra location</p>
              <Link to="/register">
                <button className="w-full h-10 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90 active:scale-[0.98]"
                  style={{ background: 'linear-gradient(135deg, #2563EB, #1D4ED8)', color: 'white', boxShadow: '0 4px 16px rgba(37,99,235,0.3)' }}>
                  <ArrowRight size={14} /> Get Started Now
                </button>
              </Link>
            </div>
          </div>
          
          {/* Partner Logo - FABG */}
          <div className="pt-6 mt-auto border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
            <p className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: 'rgba(148,163,184,0.5)' }}>
              Official Partner
            </p>
            <img 
              src="/fabg-logo.png" 
              alt="Full Arch Buying Group" 
              className="h-12 opacity-70 hover:opacity-100 transition-opacity"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="hidden lg:block w-px" style={{ background: 'rgba(255,255,255,0.07)' }} />

      {/* RIGHT: Auth panel */}
      <div className="flex-1 lg:max-w-[480px] flex flex-col justify-center px-8 py-12"
        style={{ background: '#0D1526' }}>
        <div className="w-full max-w-sm mx-auto">
          <div className="lg:hidden mb-8"><Logo size={36} showText light /></div>

          {!showSignIn ? (
            /* DEFAULT VIEW: Get Started prominent */
            <>
              {/* Primary CTA */}
              <div className="mb-8">
                <h2 className="font-heading text-2xl font-bold text-white mb-1" style={{ letterSpacing: '-0.02em' }}>Start tracking today</h2>
                <p className="text-sm mb-5" style={{ color: 'rgba(148,163,184,0.7)' }}>Set up your practice in 60 seconds</p>

                <Link to="/register" className="block mb-3">
                  <button className="w-full h-12 rounded-xl font-bold text-base flex items-center justify-center gap-2.5 transition-all active:scale-[0.98]"
                    style={{
                      background: 'linear-gradient(135deg, #2563EB, #1E40AF)',
                      color: 'white',
                      boxShadow: '0 4px 24px rgba(37,99,235,0.4), 0 1px 4px rgba(37,99,235,0.2)'
                    }}>
                    <ArrowRight size={17} /> Create Account — $249/mo
                  </button>
                </Link>

                <div className="flex items-center gap-2 mb-6">
                  <CheckCircle size={13} className="text-emerald-400 flex-shrink-0" />
                  <span className="text-xs" style={{ color: 'rgba(148,163,184,0.6)' }}>
                    First location included · +$29/mo extra locations · Cancel anytime
                  </span>
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3 mb-5">
                <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.08)' }} />
                <span className="text-xs font-medium" style={{ color: 'rgba(100,116,139,0.5)' }}>RETURNING USER</span>
                <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.08)' }} />
              </div>

              {/* Sign In secondary */}
              <button
                onClick={() => setShowSignIn(true)}
                className="w-full h-11 rounded-xl font-semibold text-sm transition-all hover:bg-white/8 active:scale-[0.98] mb-5"
                style={{ border: '1.5px solid rgba(255,255,255,0.15)', color: 'white' }}
              >
                Sign In to Dashboard
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3 mb-5">
                <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.06)' }} />
                <span className="text-xs" style={{ color: 'rgba(100,116,139,0.4)' }}>or</span>
                <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.06)' }} />
              </div>

              {/* Demo */}
              <button onClick={handleDemoLogin} disabled={demoLoading}
                className="w-full h-10 flex items-center justify-center gap-2 rounded-xl text-sm font-medium transition-all hover:bg-white/5"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(148,163,184,0.7)' }}
                data-testid="demo-login-button">
                {demoLoading
                  ? <><Loader2 size={14} className="animate-spin" /> Loading demo…</>
                  : <><Sparkles size={14} /> Explore Demo — No account needed</>}
              </button>
              
              <p className="text-center mt-6 text-xs" style={{ color: 'rgba(100,116,139,0.45)' }}>
                90 days of sample data · Full feature access
              </p>
            </>
          ) : (
            /* SIGN IN FORM */
            <>
              <button onClick={() => setShowSignIn(false)}
                className="flex items-center gap-1.5 text-xs mb-7 transition-colors hover:text-white"
                style={{ color: 'rgba(100,116,139,0.6)' }}>
                ← Back
              </button>
              <h2 className="font-heading text-2xl font-bold text-white mb-1" style={{ letterSpacing: '-0.02em' }}>Welcome back</h2>
              <p className="text-sm mb-7" style={{ color: 'rgba(148,163,184,0.7)' }}>Sign in to your dashboard</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Email Address</Label>
                  <Input
                    data-testid="login-username-input"
                    value={username} onChange={e => setUsername(e.target.value)}
                    placeholder="you@practice.com" type="email" style={inputStyle} className="h-11" autoComplete="username" />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Password</Label>
                    <Link to="/forgot-password" className="text-xs hover:text-blue-300 transition-colors" style={{ color: 'rgba(100,116,139,0.6)' }}>
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Input
                      data-testid="login-password-input"
                      type={showPw ? 'text' : 'password'} value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="••••••••" style={inputStyle} className="h-11 pr-10" autoComplete="current-password" />
                    <button type="button" onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                      style={{ color: 'rgba(148,163,184,0.4)' }}>
                      {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 gap-2 font-semibold"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading
                    ? <><Loader2 size={15} className="animate-spin" /> Signing in…</>
                    : <><ArrowRight size={15} /> Sign In</>}
                </Button>
              </form>
              <p className="text-center mt-6 text-sm" style={{ color: 'rgba(100,116,139,0.6)' }}>
                No account?{' '}
                <Link to="/register" className="text-blue-400 font-semibold hover:text-blue-300">Create one →</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
